import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import {
  AirplaneTakeoffIcon,
  AirplaneLandingIcon,
} from "@/components/Icons/rtlReadyIcons";
import { AirportType } from "@/features/flights/types/types";
import { LocationPicker } from "@/features/search/components/LocationPicker";
import {
  type SearchSource,
  rudderAnalytics,
  FlightSearchTrackingEvents,
} from "@/lib/rudderstack";

import { AirportSearch } from "../components/AirportSearch/AirportSearch";
import { SwapValuesButton } from "../components/SwapValuesButton/SwapValuesButton";
import type { FlightSearchForm } from "../FlightSearchForm";

type TripLocationPickerProps = {
  form: FlightSearchForm;
  analyticsSource: SearchSource;
  showErrorsInline?: boolean;
};

export function FlightLocationPicker({
  analyticsSource,
  form,
  showErrorsInline,
}: TripLocationPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [targetField, setTargetField] = useState<AirportType>(AirportType.FROM);

  const [from, to] = useWatch({
    control: form.control,
    name: ["from", "to"],
  });

  const swapAirportValues = () => {
    const currentFrom = form.getValues("from");
    const currentTo = form.getValues("to");

    form.setValue("from", currentTo);
    form.setValue("to", currentFrom);
  };

  return (
    <>
      <div className="relative flex flex-col gap-[inherit]">
        <LocationPicker
          startIcon={<AirplaneTakeoffIcon />}
          label={intl.formatMessage({
            id: "flights.search.from",
            defaultMessage: "From",
          })}
          value={from}
          onPickerOpen={() => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_ORIGIN_INPUT,
              { source: analyticsSource },
            );

            setTargetField(AirportType.FROM);
            setIsDrawerOpen(true);
          }}
          errorMessage={
            showErrorsInline ? form.formState.errors.from?.message : undefined
          }
        />

        <SwapValuesButton
          onClick={swapAirportValues}
          className="absolute end-5 top-1/2 z-10 -translate-y-1/2"
        />

        <LocationPicker
          startIcon={<AirplaneLandingIcon />}
          label={intl.formatMessage({
            id: "flights.search.to",
            defaultMessage: "To",
          })}
          value={to}
          onPickerOpen={() => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_DESTINATION_INPUT,
              { source: analyticsSource },
            );

            setTargetField(AirportType.TO);
            setIsDrawerOpen(true);
          }}
          errorMessage={
            showErrorsInline
              ? form.formState.errors.to?.name?.message
              : undefined
          }
        />
      </div>

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
        heightOffset={24}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <DrawerTopBar
          title={
            targetField === AirportType.FROM
              ? intl.formatMessage({
                  id: "flights.search.flyingFrom",
                  defaultMessage: "Flying from",
                })
              : intl.formatMessage({
                  id: "flights.search.flyingTo",
                  defaultMessage: "Flying to",
                })
          }
          onCloseClick={() => setIsDrawerOpen(false)}
        />

        <AirportSearch
          onChange={(newValue) => {
            form.setValue(targetField, newValue);

            setIsDrawerOpen(false);
          }}
          excludedLocation={targetField === AirportType.FROM ? to : from}
          field={targetField}
          analyticsSource={analyticsSource}
        />
      </SwipeableDrawer>
    </>
  );
}
